import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, imgUrl } from "../../lib/api";
import HomeSection from "../../components/store/HomeSection";
import useSeo from "../../hooks/useSeo";
import { Share2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function CampaignPage() {
  const { slug } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const { data } = await api.get(`/campaigns/${slug}`);
        if (alive) setCampaign(data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "This campaign is no longer live.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  const canonical = typeof window !== "undefined"
    ? `${window.location.origin}${window.location.pathname}`
    : "";

  const jsonLd = useMemo(() => {
    if (!campaign) return null;
    const items = (campaign.featured_products || []).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: typeof window !== "undefined" ? `${window.location.origin}/product/${p.slug}` : `/product/${p.slug}`,
      name: p.title,
    }));
    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: campaign.meta_title || campaign.title,
      description: campaign.meta_description || campaign.subtitle,
      url: canonical,
      mainEntity: items.length
        ? { "@type": "ItemList", itemListElement: items }
        : undefined,
    };
  }, [campaign, canonical]);

  useSeo({
    title: campaign?.meta_title || campaign?.title || "Campaign · KalkiBazaar",
    description: campaign?.meta_description || campaign?.subtitle,
    keywords: campaign?.meta_keywords,
    canonical,
    ogImage: campaign?.og_image_url || campaign?.hero_image_url,
    jsonLd,
  });

  const onShare = async () => {
    const shareData = {
      title: campaign?.title,
      text: campaign?.subtitle || campaign?.meta_description,
      url: canonical,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch { /* user dismissed */ }
    }
    try {
      await navigator.clipboard.writeText(canonical);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="campaign-loading">
        <div className="text-sm text-[hsl(var(--kb-muted))]">Loading campaign…</div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-20 text-center" data-testid="campaign-error">
        <div className="overline mb-3">404</div>
        <h1 className="font-display text-4xl mb-4">Campaign not found</h1>
        <p className="text-[hsl(var(--kb-muted))] mb-8">{error || "This link may have expired."}</p>
        <Link to="/shop" className="kb-btn-primary">Back to the Bazaar</Link>
      </div>
    );
  }

  const hero = campaign.hero_image_url;
  const mhero = campaign.mobile_hero_image_url || hero;

  return (
    <div data-testid={`campaign-page-${campaign.slug}`}>
      {/* Hero */}
      {hero && (
        <section className="relative h-[70vh] md:h-[86vh] overflow-hidden">
          <picture>
            <source media="(max-width: 767px)" srcSet={imgUrl(mhero)} />
            <img
              src={imgUrl(hero)}
              alt={campaign.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/70" />
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-6">
            {campaign.hero_eyebrow && (
              <div className="overline text-white/90 mb-6">{campaign.hero_eyebrow}</div>
            )}
            <h1 className="font-display text-4xl sm:text-6xl lg:text-[96px] leading-[0.98] max-w-5xl font-light tracking-tighter">
              {campaign.title}
            </h1>
            {campaign.subtitle && (
              <p className="max-w-xl mt-6 text-white/85 text-base md:text-lg leading-relaxed">
                {campaign.subtitle}
              </p>
            )}
            <div className="flex flex-wrap gap-3 mt-8 justify-center">
              {campaign.hero_cta_url && campaign.hero_cta_label && (
                <Link
                  to={campaign.hero_cta_url}
                  className="kb-btn-primary"
                  data-testid="campaign-hero-cta"
                >
                  {campaign.hero_cta_label}
                </Link>
              )}
              <button
                className="kb-btn-outline border-white text-white hover:bg-white/10 flex items-center gap-2"
                onClick={onShare}
                data-testid="campaign-share-btn"
                type="button"
              >
                <Share2 size={14} /> Share
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Intro */}
      {campaign.intro_body && (
        <section className="px-6 md:px-12 lg:px-24 py-16 max-w-4xl">
          <p className="text-[hsl(var(--kb-text))] text-lg leading-relaxed">
            {campaign.intro_body}
          </p>
        </section>
      )}

      {/* Sections */}
      {(campaign.sections || []).map((s, i) => (
        <HomeSection
          key={`${campaign.id}-${i}-${s.type}`}
          section={{ ...s, id: `${campaign.id}-${i}`, slug: `${campaign.slug}-${i}` }}
        />
      ))}

      {/* Share footer */}
      <section className="px-6 md:px-12 lg:px-24 py-12 border-t border-[hsl(var(--kb-border))]">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="overline mb-1">Share this edit</div>
            <div className="text-sm text-[hsl(var(--kb-muted))] break-all">{canonical}</div>
          </div>
          <div className="flex gap-3">
            <button
              className="kb-btn-outline flex items-center gap-2"
              onClick={onShare}
              data-testid="campaign-share-bottom"
              type="button"
            >
              <Share2 size={14} /> Share
            </button>
            <button
              className="kb-btn-outline flex items-center gap-2"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(canonical);
                  toast.success("Link copied");
                } catch {
                  toast.error("Could not copy link");
                }
              }}
              data-testid="campaign-copy-link"
              type="button"
            >
              <Copy size={14} /> Copy link
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
